<script setup lang="ts">
import VsText from '@/components/vision/VsText.vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { MultiselectOption } from '@ironsight/types'
import { PaginatedDataProvider } from '@/models/types/paginated-data-provider'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { useMultiselectSelectAll } from '@/components/jobs/job-search-page/useMultiselectSelectAll'

interface Props {
  value: MultiselectOption[]
  label?: string
  dataProvider: PaginatedDataProvider
  canBeEmpty?: boolean
  supportsSelectAll?: boolean
}

const HAS_VALUE_SENTINEL = '__HAS_VALUE__'
const EMPTY_LABEL = 'Is empty'
const HAS_VALUE_LABEL = 'Is not empty'
const SENTINEL_KEY = Symbol('load-more-sentinel')

type OptionItem = MultiselectOption & { isFirstWithDivider?: boolean }
type SentinelItem = {
  value: typeof SENTINEL_KEY
  label: string
  isSentinel: true
}
type VirtualScrollItem = OptionItem | SentinelItem

const props = withDefaults(defineProps<Props>(), {
  label: '',
  canBeEmpty: false,
  supportsSelectAll: false,
})

const emit = defineEmits<{
  (e: 'input', value: MultiselectOption[]): void
}>()

const DEFAULT_SCROLLER_HEIGHT = 300

let observer: IntersectionObserver | null = null

const searchText = ref<string>('')
const searchInputRef = ref<InstanceType<typeof VsTextInput> | null>(null)
const virtualScrollRef = ref<any>()
const loadMoreSentinel = ref<HTMLElement | null>(null)
const displayedOptions = ref<OptionItem[]>([])
const containerEl = ref<HTMLDivElement | null>(null)
const scrollerHeight = ref(DEFAULT_SCROLLER_HEIGHT)

const checkedBoxes = ref<Array<string | number>>([])

const itemsForVirtualScroll = computed<VirtualScrollItem[]>(() => {
  const baseItems: VirtualScrollItem[] = displayedOptions.value
  if (props.dataProvider.hasMore.value) {
    return [...baseItems, { value: SENTINEL_KEY, label: '_internal_sentinel_', isSentinel: true }]
  }
  return baseItems
})

function resolveOption(value: string | number): MultiselectOption {
  const opt = props.dataProvider.options.value.find((option) => option.value === value)
  if (opt) {
    return opt
  }
  if (value === '') {
    return { label: EMPTY_LABEL, value: '' }
  }
  if (value === HAS_VALUE_SENTINEL) {
    return { label: HAS_VALUE_LABEL, value: HAS_VALUE_SENTINEL }
  }
  return { label: String(value), value }
}

const filteredOptions = computed<MultiselectOption[]>(() => {
  const opts = props.dataProvider.options.value
  const shouldShowEmptyOption =
    props.canBeEmpty && !searchText.value && !opts.some((opt) => opt.value === '')

  if (shouldShowEmptyOption) {
    return [
      { label: EMPTY_LABEL, value: '' },
      { label: HAS_VALUE_LABEL, value: HAS_VALUE_SENTINEL },
      ...opts,
    ]
  }
  return opts
})

const {
  isSelectAllMode,
  isItemChecked,
  toggleItem,
  selectAllFiltered: selectAllFilteredValue,
  deselectAllFiltered: deselectAllFilteredValue,
  areAllFilteredSelected,
  mapCheckedBoxValues,
} = useMultiselectSelectAll({
  value: () => props.value,
  searchText,
  checkedBoxes,
  filteredOptions: () => filteredOptions.value,
  resolveOption,
  emitInput: (value) => emit('input', value),
  searchSelectAllMode: 'sentinel',
})

checkedBoxes.value = mapCheckedBoxValues(props.value)

function buildDisplayedOptionsList() {
  let options = props.dataProvider.options.value
  const shouldShowEmptyOption =
    props.canBeEmpty && !searchText.value && !options.some((opt) => opt.value === '')

  if (shouldShowEmptyOption) {
    options = [
      { label: EMPTY_LABEL, value: '' },
      { label: HAS_VALUE_LABEL, value: HAS_VALUE_SENTINEL },
      ...options,
    ]
  }

  if (isSelectAllMode.value) {
    // In select-all mode keep items in their original order — excluded
    // (unchecked) items stay in place so the list doesn't jump around.
    displayedOptions.value = options
    return
  }

  // Normal mode: selected items first
  const selectedItems: OptionItem[] = props.value.map((option) => {
    if (option.value === '') {
      return { ...option, label: EMPTY_LABEL }
    }
    if (option.value === HAS_VALUE_SENTINEL) {
      return { ...option, label: HAS_VALUE_LABEL }
    }
    return option
  })
  const unselectedItems: OptionItem[] = []
  for (const opt of options) {
    if (!selectedItems.some((item) => item.value === opt.value)) {
      unselectedItems.push(opt)
    }
  }
  if (selectedItems.length > 0 && unselectedItems.length > 0) {
    const firstUnselected = { ...unselectedItems[0], isFirstWithDivider: true }
    displayedOptions.value = [...selectedItems, firstUnselected, ...unselectedItems.slice(1)]
  } else {
    displayedOptions.value = [...selectedItems, ...unselectedItems]
  }
}

function handleItemClick(item: MultiselectOption) {
  toggleItem(item)
}

function loadMore() {
  if (props.dataProvider.hasMore.value && !props.dataProvider.isFetching.value) {
    props.dataProvider.fetchNextPage(searchText.value)
  }
}

const debouncedFetchSearchResults = useDebounceFn(async (newQuery: string) => {
  props.dataProvider.fetchNextPage(newQuery)
  if (virtualScrollRef.value && virtualScrollRef.value.scrollToIndex) {
    virtualScrollRef.value.scrollToIndex(0)
  }
}, 300)

function handleSearchInput(newQuery: string) {
  searchText.value = newQuery
  debouncedFetchSearchResults(newQuery)
  if (virtualScrollRef.value && virtualScrollRef.value.scrollToIndex) {
    virtualScrollRef.value.scrollToIndex(0)
  }
}

watch(
  () => props.dataProvider.options.value,
  () => {
    buildDisplayedOptionsList()
  },
  { deep: true },
)

watch(
  () => props.value,
  (newVal) => {
    checkedBoxes.value = mapCheckedBoxValues(newVal)
    buildDisplayedOptionsList()
  },
)

watch(
  () => loadMoreSentinel.value,
  (sentinelEl) => {
    disconnectObserver()
    const shouldAttachObserver = sentinelEl && props.dataProvider.hasMore.value

    if (shouldAttachObserver) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            loadMore()
          }
        },
        {
          root: virtualScrollRef.value?.$el,
          threshold: 0.1,
        },
      )
      observer.observe(sentinelEl)
    }
  },
)

onMounted(async () => {
  if (props.dataProvider.options.value.length === 0 && props.dataProvider.hasMore.value) {
    props.dataProvider.fetchNextPage(searchText.value)
  }
  buildDisplayedOptionsList()
  searchInputRef.value?.focus()
  nextTick(() => {
    if (containerEl.value) {
      const rect = containerEl.value.getBoundingClientRect()
      const height = window.innerHeight - rect.top
      scrollerHeight.value = Math.max(DEFAULT_SCROLLER_HEIGHT, height)
    }
  })
})

onUnmounted(() => {
  disconnectObserver()
})

function disconnectObserver() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

function selectAllFiltered() {
  selectAllFilteredValue()
  buildDisplayedOptionsList()
}

function deselectAllFiltered() {
  deselectAllFilteredValue()
  buildDisplayedOptionsList()
}

defineExpose(
  props.supportsSelectAll
    ? {
        supportsSelectAll: true,
        filteredOptions,
        selectAllFiltered,
        deselectAllFiltered,
        areAllFilteredSelected,
      }
    : {},
)
</script>

<template>
  <div ref="containerEl" class="filter-options-container">
    <div class="filter-search">
      <vs-text-input
        ref="searchInputRef"
        :value="searchText"
        :label="props.label || 'Search'"
        @input="handleSearchInput"
      >
        <template #append>
          <v-icon>search</v-icon>
        </template>
      </vs-text-input>
    </div>

    <div v-if="!displayedOptions.length" class="empty-results">
      <vs-text center type="subdued">No results</vs-text>
    </div>

    <v-container v-if="props.dataProvider.isLoading.value" class="my-2">
      <v-row justify="center">
        <vs-text center>loading...</vs-text>
      </v-row>
    </v-container>

    <v-virtual-scroll
      v-else
      ref="virtualScrollRef"
      :items="itemsForVirtualScroll"
      class="virtual-scroll-container"
      :height="scrollerHeight"
      item-height="40"
    >
      <template #default="{ item }">
        <div v-if="item.isSentinel" :key="item.value" ref="loadMoreSentinel">
          <v-container v-if="props.dataProvider.isFetching.value" class="my-2">
            <v-row justify="center">
              <vs-text center>loading...</vs-text>
            </v-row>
          </v-container>
        </div>
        <v-list-item
          v-else
          :key="item.value"
          dense
          class="list-item"
          :class="{ 'item-with-divider': item.isFirstWithDivider }"
          @click="handleItemClick(item)"
        >
          <v-list-item-action>
            <v-checkbox
              :input-value="isItemChecked(item)"
              color="var(--color-blue)"
              hide-details
              readonly
            />
          </v-list-item-action>
          <v-list-item-content>
            <vs-text :title="item.label" truncate>{{ item.label }}</vs-text>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<style scoped>
.filter-options-container {
  background: white;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filter-search {
  border-radius: 8px !important;
  max-width: 100%;
}

.selected-count {
  font-size: 12px;
  color: var(--color-grey);
  background-color: white;
  border-bottom: 1px solid var(--color-grey--lighter);
  display: flex;
}

.virtual-scroll-container {
  overflow: auto;
  white-space: nowrap;
  background: white;
  flex: 1;
}

.empty-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: var(--color-grey);
}

.list-item {
  transition: background-color 0.15s ease;
  padding: 0 12px !important;
  margin-bottom: 2px;
}

.list-item.item-with-divider {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid var(--color-grey--lighter);
}

.list-item :deep(.v-list-item__action) {
  margin-right: 16px;
}

:deep(.v-virtual-scroll__item .v-divider) {
  margin: 32px 0 20px 0;
}
</style>
